import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import {
  purchaseApprovalService,
  type ApprovalRequest,
  type ApprovalStats,
  type AuditEvent,
} from '@/services/purchaseApprovalService';

function formatINR(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function nowISO() {
  return new Date().toISOString();
}

const APPROVAL_STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  SENT_BACK: 'bg-purple-50 text-purple-700 border-purple-200',
  ESCALATED: 'bg-orange-50 text-orange-700 border-orange-200',
  SUPERSEDED: 'bg-slate-100 text-slate-500 border-slate-200',
};

const APPROVAL_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Awaiting Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SENT_BACK: 'Sent Back for Edit',
  ESCALATED: 'Escalated',
  SUPERSEDED: 'Superseded',
};

const AUDIT_ACTION_STYLES: Record<string, string> = {
  SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  SENT_BACK: 'bg-purple-50 text-purple-700 border-purple-200',
  ESCALATED: 'bg-orange-50 text-orange-700 border-orange-200',
  AUTO_APPROVED: 'bg-slate-100 text-slate-600 border-slate-200',
};

const AUDIT_ACTION_LABELS: Record<string, string> = {
  SUBMITTED: 'Submitted for Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SENT_BACK: 'Sent Back',
  ESCALATED: 'Escalated',
  AUTO_APPROVED: 'Auto Approved',
};

// ─── Timeout display ──────────────────────────────────────────────────────────
function TimeoutCell({ request: ar }: { request: ApprovalRequest }) {
  if (ar.status !== 'PENDING' || !ar.timeoutAt) return <span className="text-slate-300 text-xs">—</span>;

  const now = new Date();
  const timeout = new Date(ar.timeoutAt);
  const diffMs = timeout.getTime() - now.getTime();
  const diffHrs = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.abs(Math.round(diffMs / (1000 * 60 * 60 * 24)));

  if (diffMs < 0) {
    return <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full whitespace-nowrap">Overdue {diffDays}d</span>;
  }
  if (diffHrs < 12) {
    return <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap">{diffHrs} hrs remaining</span>;
  }
  return <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full whitespace-nowrap">{diffHrs} hrs remaining</span>;
}

// ─── Summary Card ─────────────────────────────────────────────────────────────
function SummaryCard({ icon, label, value, sub, color }: { icon: string; label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 flex items-start gap-4">
      <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${color}`}>
        <i className={`${icon} text-xl`} />
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-[#1e293b] mt-0.5">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Approve Modal ────────────────────────────────────────────────────────────
function ApproveModal({ request: ar, isBulk, bulkCount, onClose, onConfirm }: { request?: ApprovalRequest; isBulk?: boolean; bulkCount?: number; onClose: () => void; onConfirm: (comments: string) => Promise<boolean | void> | void }) {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await onConfirm(comments);
      if (res !== false) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isBulk) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-[#e2e8f0] bg-emerald-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-100">
                <i className="ri-check-double-line text-xl text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1e293b]">Approve {bulkCount} Purchase Orders?</h3>
                <p className="text-xs text-slate-500">This will approve all selected POs</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Comments (applied to all)</label>
              <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} placeholder="Add any notes..." className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg resize-none focus:outline-none focus:border-emerald-400" />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-[#e2e8f0] flex justify-end gap-2">
            <button onClick={onClose} disabled={submitting} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap disabled:opacity-50">Cancel</button>
            <button onClick={handleConfirm} disabled={submitting} className="h-9 px-4 rounded-lg bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-700 cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50">
              {submitting && <i className="ri-loader-4-line animate-spin" />}
              {submitting ? 'Approving...' : 'Confirm Approve All'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ar) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#e2e8f0] bg-emerald-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-100">
              <i className="ri-check-line text-xl text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1e293b]">Approve {ar.poNumber}</h3>
              <p className="text-xs text-slate-500">Confirm approval for this purchase order</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-xs text-slate-400">Supplier</span><p className="font-medium text-[#1e293b]">{ar.supplierName}</p></div>
            <div><span className="text-xs text-slate-400">Amount</span><p className="font-bold text-[#1e293b]">{formatINR(ar.poAmount)}</p></div>
            <div><span className="text-xs text-slate-400">Requested By</span><p className="text-[#1e293b]">{ar.requestedBy}</p></div>
            <div><span className="text-xs text-slate-400">Role</span><p className="text-[#1e293b]">{ar.approverRole}</p></div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Comments (optional)</label>
            <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} placeholder="Add any notes..." className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg resize-none focus:outline-none focus:border-emerald-400" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#e2e8f0] flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap disabled:opacity-50">Cancel</button>
          <button onClick={handleConfirm} disabled={submitting} className="h-9 px-4 rounded-lg bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-700 cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50">
            {submitting && <i className="ri-loader-4-line animate-spin" />}
            {submitting ? 'Approving...' : 'Confirm Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Reject Modal ─────────────────────────────────────────────────────────────
function RejectModal({ request: ar, isBulk, bulkCount, onClose, onConfirm }: { request?: ApprovalRequest; isBulk?: boolean; bulkCount?: number; onClose: () => void; onConfirm: (reason: string) => Promise<boolean | void> | void }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await onConfirm(reason);
      if (res !== false) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isBulk) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-[#e2e8f0] bg-red-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
                <i className="ri-close-circle-line text-xl text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1e293b]">Reject {bulkCount} Purchase Orders?</h3>
                <p className="text-xs text-slate-500">Provide a reason for rejection</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Rejection Reason <span className="text-red-500">*</span></label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Explain why these POs are being rejected..." className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg resize-none focus:outline-none focus:border-red-400" />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-[#e2e8f0] flex justify-end gap-2">
            <button onClick={onClose} disabled={submitting} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap disabled:opacity-50">Cancel</button>
            <button onClick={handleConfirm} disabled={!reason.trim() || submitting} className="h-9 px-4 rounded-lg bg-red-600 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              {submitting && <i className="ri-loader-4-line animate-spin" />}
              {submitting ? 'Rejecting...' : 'Confirm Reject All'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ar) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#e2e8f0] bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
              <i className="ri-close-line text-xl text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1e293b]">Reject {ar.poNumber}</h3>
              <p className="text-xs text-slate-500">Provide a reason for rejection</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-xs text-slate-400">Supplier</span><p className="font-medium text-[#1e293b]">{ar.supplierName}</p></div>
            <div><span className="text-xs text-slate-400">Amount</span><p className="font-bold text-[#1e293b]">{formatINR(ar.poAmount)}</p></div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Rejection Reason <span className="text-red-500">*</span></label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Explain why this PO is being rejected..." className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg resize-none focus:outline-none focus:border-red-400" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#e2e8f0] flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap disabled:opacity-50">Cancel</button>
          <button onClick={handleConfirm} disabled={!reason.trim() || submitting} className="h-9 px-4 rounded-lg bg-red-600 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            {submitting && <i className="ri-loader-4-line animate-spin" />}
            {submitting ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Send Back Modal ──────────────────────────────────────────────────────────
function SendBackModal({ request: ar, onClose, onConfirm }: { request: ApprovalRequest; onClose: () => void; onConfirm: (notes: string) => Promise<boolean | void> | void }) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await onConfirm(notes);
      if (res !== false) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#e2e8f0] bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-purple-100">
              <i className="ri-arrow-go-back-line text-xl text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1e293b]">Send Back for Revision</h3>
              <p className="text-xs text-slate-500">PO will be returned for editing</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
            <i className="ri-information-line mr-1" />
            PO will be set to <strong>Revision Needed</strong> status and can be edited and re-submitted by the creator.
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Revision Notes <span className="text-red-500">*</span></label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What needs to be changed? e.g. Update rates, change supplier..." className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg resize-none focus:outline-none focus:border-purple-400" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#e2e8f0] flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap disabled:opacity-50">Cancel</button>
          <button onClick={handleConfirm} disabled={!notes.trim() || submitting} className="h-9 px-4 rounded-lg bg-purple-600 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            {submitting && <i className="ri-loader-4-line animate-spin" />}
            {submitting ? 'Sending Back...' : 'Send Back'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Slide-Over ────────────────────────────────────────────────────────
function DetailSlideOver({ request: ar, onClose, onApprove, onReject, onSendBack }: { request: ApprovalRequest; onClose: () => void; onApprove: () => void; onReject: () => void; onSendBack: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e2e8f0] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-[#1e293b]">{ar.poNumber}</h3>
            <p className="text-xs text-slate-500">Approval Request Details</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 cursor-pointer text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">PO Details</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-xs text-slate-400">PO Number</span><p className="font-semibold text-[#4f46e5]">{ar.poNumber}</p></div>
              <div><span className="text-xs text-slate-400">Supplier</span><p className="font-medium text-[#1e293b]">{ar.supplierName}</p></div>
              <div><span className="text-xs text-slate-400">Amount</span><p className="font-bold text-[#1e293b]">{formatINR(ar.poAmount)}</p></div>
              <div><span className="text-xs text-slate-400">Requested By</span><p className="text-[#1e293b]">{ar.requestedBy}</p></div>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Approval Details</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-xs text-slate-400">Approver Role</span><p className="font-medium text-[#1e293b]">{ar.approverRole}</p></div>
              <div><span className="text-xs text-slate-400">Approver Name</span><p className="font-medium text-[#1e293b]">{ar.approverName}</p></div>
              <div><span className="text-xs text-slate-400">Requested At</span><p className="text-[#1e293b]">{formatDateTime(ar.requestedAt)}</p></div>
              {ar.timeoutAt && <div><span className="text-xs text-slate-400">Timeout At</span><p className="text-[#1e293b]">{formatDateTime(ar.timeoutAt)}</p></div>}
              <div className="col-span-2">
                <span className="text-xs text-slate-400">Status</span>
                <span className={`inline-block ml-2 text-xs px-2 py-0.5 rounded-full border font-medium ${APPROVAL_STATUS_STYLES[ar.status]}`}>{APPROVAL_STATUS_LABELS[ar.status]}</span>
              </div>
              {ar.comments && <div className="col-span-2"><span className="text-xs text-slate-400">Comments</span><p className="text-sm text-[#1e293b] mt-0.5 bg-slate-50 p-2 rounded-lg">{ar.comments}</p></div>}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">ITEMS PREVIEW</h4>
            <div className="border border-[#e2e8f0] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-[#e2e8f0]">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(ar.items || []).length > 0 ? (
                    ar.items!.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td className="px-3 py-2 font-medium text-[#1e293b]">{item.itemName}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{item.qty} {item.unit || 'Pcs'}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{formatINR(item.rate)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#1e293b]">{formatINR(item.total || item.qty * item.rate)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center text-slate-400">No items available preview</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#e2e8f0] px-6 py-4 flex items-center justify-between z-10">
          <button onClick={() => { onClose(); navigate('/purchase/orders'); }} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
            <i className="ri-external-link-line mr-1.5" />View Full PO
          </button>
          {ar.status === 'PENDING' && (
            <div className="flex items-center gap-2">
              <button onClick={onSendBack} className="h-9 px-3 rounded-lg border border-purple-200 text-sm font-medium text-purple-700 hover:bg-purple-50 cursor-pointer whitespace-nowrap"><i className="ri-arrow-go-back-line mr-1" />Send Back</button>
              <button onClick={onReject} className="h-9 px-3 rounded-lg border border-red-200 text-sm font-medium text-red-700 hover:bg-red-50 cursor-pointer whitespace-nowrap"><i className="ri-close-line mr-1" />Reject</button>
              <button onClick={onApprove} className="h-9 px-4 rounded-lg bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-700 cursor-pointer whitespace-nowrap"><i className="ri-check-line mr-1" />Approve</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
type TabType = 'all' | 'dashboard' | 'audit';
type FilterStatus = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'SENT_BACK' | 'ESCALATED';
type FilterApprover = 'ALL' | 'PURCHASE_MANAGER' | 'DIRECTOR' | 'MD';

export default function POApprovalsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { selectedWarehouseId } = useWarehouseStore();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('ALL');
  const [approverFilter, setApproverFilter] = useState<FilterApprover>('ALL');
  const [search, setSearch] = useState('');

  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [stats, setStats] = useState<ApprovalStats>({ pending: 0, approvedToday: 0, rejected: 0, avgHours: '—' });
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Audit trail filters
  const [auditSearch, setAuditSearch] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState<string>('ALL');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modals
  const [approveTarget, setApproveTarget] = useState<ApprovalRequest | null>(null);
  const [bulkApprove, setBulkApprove] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<ApprovalRequest | null>(null);
  const [bulkReject, setBulkReject] = useState(false);
  const [sendBackTarget, setSendBackTarget] = useState<ApprovalRequest | null>(null);
  const [detailTarget, setDetailTarget] = useState<ApprovalRequest | null>(null);

  // ── Load live data from DB APIs
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const whId = selectedWarehouseId && selectedWarehouseId !== 'ALL' && selectedWarehouseId !== 'all' ? selectedWarehouseId : undefined;
      const [reqData, statsData, auditData] = await Promise.all([
        purchaseApprovalService.getRequests({
          status: statusFilter !== 'ALL' ? statusFilter : undefined,
          role: approverFilter !== 'ALL' ? approverFilter : undefined,
          search: search || undefined,
          warehouseId: whId,
        }),
        purchaseApprovalService.getStats({ warehouseId: whId }),
        purchaseApprovalService.getAuditTrail({
          action: auditActionFilter !== 'ALL' ? auditActionFilter : undefined,
          search: auditSearch || undefined,
          warehouseId: whId,
        }),
      ]);
      setRequests(reqData);
      setStats(statsData);
      setAuditEvents(auditData);
    } catch (err) {
      console.warn('Failed to load approval data from server:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, approverFilter, search, auditActionFilter, auditSearch, selectedWarehouseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pendingList = useMemo(() => requests.filter((r) => r.status === 'PENDING'), [requests]);

  // ── Select / deselect
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingList.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingList.map((r) => r.id)));
    }
  };

  // ── Actions
  const handleApprove = async (comments: string) => {
    if (!approveTarget) return;
    const target = approveTarget;
    const targetId = target.id;
    // Hide popup immediately & optimistically update status
    setApproveTarget(null);
    setDetailTarget(null);
    setRequests((prev) =>
      prev.map((r) => (r.id === targetId || (target.poId && r.poId === target.poId) ? { ...r, status: 'APPROVED', comments } : r))
    );
    setStats((prev) => ({
      ...prev,
      pending: Math.max(0, prev.pending - 1),
      approvedToday: prev.approvedToday + 1,
    }));

    try {
      const res = await purchaseApprovalService.approveRequests([targetId], comments);
      if (res && res.success === false) {
        showToast(res.message || 'Failed to approve request', 'error');
        await loadData();
        return false;
      }
      showToast(`${target.poNumber} approved successfully`, 'success');
      await loadData();
    } catch (err: any) {
      showToast(err.message || 'Failed to approve request', 'error');
      await loadData();
      return false;
    }
  };

  const handleBulkApprove = async (comments: string) => {
    const selectedRequests = pendingList.filter((r) => selectedIds.has(r.id));
    if (selectedRequests.length === 0) return;
    const idsArray = Array.from(selectedIds);
    // Hide popup immediately & optimistically update status
    setSelectedIds(new Set());
    setBulkApprove(false);
    setDetailTarget(null);
    setRequests((prev) =>
      prev.map((r) => (idsArray.includes(r.id) ? { ...r, status: 'APPROVED', comments } : r))
    );
    setStats((prev) => ({
      ...prev,
      pending: Math.max(0, prev.pending - selectedRequests.length),
      approvedToday: prev.approvedToday + selectedRequests.length,
    }));

    try {
      const res = await purchaseApprovalService.approveRequests(idsArray, comments);
      if (res && res.success === false) {
        showToast(res.message || 'Failed to bulk approve requests', 'error');
        await loadData();
        return false;
      }
      showToast(`${selectedRequests.length} POs approved successfully`, 'success');
      await loadData();
    } catch (err: any) {
      showToast(err.message || 'Failed to bulk approve requests', 'error');
      await loadData();
      return false;
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    const target = rejectTarget;
    const targetId = target.id;
    // Hide popup immediately & optimistically update status
    setRejectTarget(null);
    setDetailTarget(null);
    setRequests((prev) =>
      prev.map((r) => (r.id === targetId || (target.poId && r.poId === target.poId) ? { ...r, status: 'REJECTED', comments: reason } : r))
    );
    setStats((prev) => ({
      ...prev,
      pending: Math.max(0, prev.pending - 1),
      rejected: prev.rejected + 1,
    }));

    try {
      const res = await purchaseApprovalService.rejectRequests([targetId], reason);
      if (res && res.success === false) {
        showToast(res.message || 'Failed to reject request', 'error');
        await loadData();
        return false;
      }
      showToast(`${target.poNumber} rejected`, 'success');
      await loadData();
    } catch (err: any) {
      showToast(err.message || 'Failed to reject request', 'error');
      await loadData();
      return false;
    }
  };

  const handleBulkReject = async (reason: string) => {
    const selectedRequests = pendingList.filter((r) => selectedIds.has(r.id));
    if (selectedRequests.length === 0) return;
    const idsArray = Array.from(selectedIds);
    // Hide popup immediately & optimistically update status
    setSelectedIds(new Set());
    setBulkReject(false);
    setDetailTarget(null);
    setRequests((prev) =>
      prev.map((r) => (idsArray.includes(r.id) ? { ...r, status: 'REJECTED', comments: reason } : r))
    );
    setStats((prev) => ({
      ...prev,
      pending: Math.max(0, prev.pending - selectedRequests.length),
      rejected: prev.rejected + selectedRequests.length,
    }));

    try {
      const res = await purchaseApprovalService.rejectRequests(idsArray, reason);
      if (res && res.success === false) {
        showToast(res.message || 'Failed to bulk reject requests', 'error');
        await loadData();
        return false;
      }
      showToast(`${selectedRequests.length} POs rejected`, 'success');
      await loadData();
    } catch (err: any) {
      showToast(err.message || 'Failed to bulk reject requests', 'error');
      await loadData();
      return false;
    }
  };

  const handleSendBack = async (notes: string) => {
    if (!sendBackTarget) return;
    const target = sendBackTarget;
    const targetId = target.id;
    // Hide popup immediately & optimistically update status
    setSendBackTarget(null);
    setDetailTarget(null);
    setRequests((prev) =>
      prev.map((r) => (r.id === targetId || (target.poId && r.poId === target.poId) ? { ...r, status: 'SENT_BACK', comments: notes } : r))
    );
    setStats((prev) => ({
      ...prev,
      pending: Math.max(0, prev.pending - 1),
    }));

    try {
      const res = await purchaseApprovalService.sendBackRequest(targetId, notes);
      if (res && res.success === false) {
        showToast(res.message || 'Failed to send back request', 'error');
        await loadData();
        return false;
      }
      showToast(`${target.poNumber} sent back for revision`, 'success');
      await loadData();
    } catch (err: any) {
      showToast(err.message || 'Failed to send back request', 'error');
      await loadData();
      return false;
    }
  };

  const handleExportAudit = () => {
    const rows = auditEvents.map((e) => ({
      'Date & Time': formatDateTime(e.dateTime),
      'PO Number': e.poNumber,
      'Amount': formatINR(e.amount),
      'Action': AUDIT_ACTION_LABELS[e.action],
      'Performed By': e.performedBy,
      'Role': e.role,
      'Comments': e.comments ?? '',
    }));
    const header = Object.keys(rows[0] ?? {}).join(',');
    const csvRows = rows.map((r) => Object.values(r).map((v) => `"${v}"`).join(','));
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PO-Audit-Log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Audit log exported to CSV', 'success');
  };

  // ── Tab labels
  const tabs: { key: TabType; label: string; icon: string }[] = [
    { key: 'all', label: 'All Approvals', icon: 'ri-shield-check-line' },
    { key: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
    { key: 'audit', label: 'Audit Trail', icon: 'ri-history-line' },
  ];

  const lb = 'block text-xs font-semibold text-slate-500 mb-1';
  const inp = 'w-full h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]';

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full space-y-5">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">PO Approvals</h1>
            <p className="text-sm text-slate-500 mt-0.5">Purchase order approval management</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/purchase/approvals/settings')}
            className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-settings-3-line" />
            Settings
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 bg-white border border-[#e2e8f0] rounded-xl p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSelectedIds(new Set()); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? 'bg-[#4f46e5] text-white shadow-sm' : 'text-slate-500 hover:text-[#1e293b] hover:bg-slate-50'}`}
            >
              <i className={tab.icon} />
              {tab.label}
              {tab.key === 'all' && stats.pending > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>{stats.pending}</span>
              )}
            </button>
          ))}
        </div>

        {/* ─── TAB: DASHBOARD ─── */}
        {activeTab === 'dashboard' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard icon="ri-time-line" label="Pending Approval" value={stats.pending} color="bg-amber-50 text-amber-600" />
              <SummaryCard icon="ri-check-double-line" label="Approved Today" value={stats.approvedToday} color="bg-emerald-50 text-emerald-600" />
              <SummaryCard icon="ri-close-circle-line" label="Rejected" value={stats.rejected} color="bg-red-50 text-red-600" />
              <SummaryCard icon="ri-speed-line" label="Avg Approval Time" value={stats.avgHours} color="bg-sky-50 text-sky-600" />
            </div>

            {/* Mini table preview */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#e2e8f0]">
                <h3 className="text-sm font-semibold text-[#1e293b]">Pending Approvals</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      {['PO Number', 'Supplier', 'Amount', 'Approver', 'Timeout', 'Status'].map((h) => (
                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap ${h === 'Amount' ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {requests.filter((r) => r.status === 'PENDING').slice(0, 10).map((r) => (
                      <tr key={r.id} className="border-b border-slate-50 hover:bg-indigo-50/20 transition-colors">
                        <td className="px-4 py-3 font-semibold text-[#4f46e5] whitespace-nowrap">{r.poNumber}</td>
                        <td className="px-4 py-3 text-[#1e293b] font-medium">{r.supplierName}</td>
                        <td className="px-4 py-3 text-right font-semibold text-[#1e293b] whitespace-nowrap">{formatINR(r.poAmount)}</td>
                        <td className="px-4 py-3 text-[#1e293b]">{r.approverName}</td>
                        <td className="px-4 py-3"><TimeoutCell request={r} /></td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${APPROVAL_STATUS_STYLES[r.status]}`}>{APPROVAL_STATUS_LABELS[r.status]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {requests.filter((r) => r.status === 'PENDING').length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <i className="ri-check-double-line text-3xl mb-2 block" />
                  <p className="text-sm">No pending approvals — all clear!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── TAB: ALL APPROVALS ─── */}
        {activeTab === 'all' && (
          <>
            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
              <div className="bg-[#4f46e5] text-white rounded-xl px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-multiple-line text-lg" />
                  <span className="text-sm font-medium">{selectedIds.size} PO{selectedIds.size !== 1 ? 's' : ''} selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setBulkApprove(true)} className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white text-emerald-700 text-xs font-semibold hover:bg-emerald-50 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-check-line" />Approve All
                  </button>
                  <button onClick={() => setBulkReject(true)} className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white text-red-700 text-xs font-semibold hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-close-line" />Reject All
                  </button>
                  <button onClick={() => setSelectedIds(new Set())} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 cursor-pointer text-white">
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
            )}

            {/* Filter bar */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div>
                  <label className={lb}>Status</label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as FilterStatus)} className={inp}>
                    <option value="ALL">All Statuses</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="SENT_BACK">Sent Back</option>
                    <option value="ESCALATED">Escalated</option>
                  </select>
                </div>
                <div>
                  <label className={lb}>Approver</label>
                  <select value={approverFilter} onChange={(e) => setApproverFilter(e.target.value as FilterApprover)} className={inp}>
                    <option value="ALL">All Approvers</option>
                    <option value="PURCHASE_MANAGER">Purchase Manager</option>
                    <option value="DIRECTOR">Director</option>
                    <option value="MD">MD</option>
                  </select>
                </div>
                <div>
                  <label className={lb}>Search</label>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="PO No / Supplier..." className="w-full h-9 pl-8 pr-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]" />
                  </div>
                </div>
                <div className="flex items-end">
                  <p className="text-xs text-slate-400"><strong className="text-[#1e293b]">{requests.length}</strong> request{requests.length !== 1 ? 's' : ''} found</p>
                </div>
              </div>
            </div>

            {/* Approval table */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-10">
                        <input
                          type="checkbox"
                          checked={pendingList.length > 0 && selectedIds.size === pendingList.length}
                          onChange={toggleSelectAll}
                          className="w-3.5 h-3.5 rounded border-slate-300 cursor-pointer accent-[#4f46e5]"
                        />
                      </th>
                      {['PO Number', 'Supplier', 'Amount', 'Requested By', 'Requested On', 'Approver', 'Timeout', 'Status', 'Actions'].map((h) => (
                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap ${['Amount'].includes(h) ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} className="border-b border-slate-50 hover:bg-indigo-50/20 transition-colors">
                        <td className="px-4 py-3">
                          {r.status === 'PENDING' ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleSelect(r.id)}
                              className="w-3.5 h-3.5 rounded border-slate-300 cursor-pointer accent-[#4f46e5]"
                            />
                          ) : (
                            <span className="w-3.5 inline-block" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold text-[#4f46e5] whitespace-nowrap">{r.poNumber}</td>
                        <td className="px-4 py-3 text-[#1e293b] font-medium">{r.supplierName}</td>
                        <td className="px-4 py-3 text-right font-semibold text-[#1e293b] whitespace-nowrap">{formatINR(r.poAmount)}</td>
                        <td className="px-4 py-3 text-slate-600">{r.requestedBy}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{formatDate(r.requestedAt)}</td>
                        <td className="px-4 py-3 text-[#1e293b]">{r.approverName}</td>
                        <td className="px-4 py-3"><TimeoutCell request={r} /></td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${APPROVAL_STATUS_STYLES[r.status]}`}>{APPROVAL_STATUS_LABELS[r.status]}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setDetailTarget(r)} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#4f46e5] rounded cursor-pointer" title="View">
                              <i className="ri-eye-line text-sm" />
                            </button>
                            {r.status === 'PENDING' && (
                              <>
                                <button onClick={() => setApproveTarget(r)} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-emerald-600 rounded cursor-pointer" title="Approve">
                                  <i className="ri-check-line text-sm" />
                                </button>
                                <button onClick={() => setRejectTarget(r)} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-600 rounded cursor-pointer" title="Reject">
                                  <i className="ri-close-line text-sm" />
                                </button>
                                <button onClick={() => setSendBackTarget(r)} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-purple-600 rounded cursor-pointer" title="Send Back">
                                  <i className="ri-arrow-go-back-line text-sm" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {requests.length === 0 && (
                <div className="text-center py-16 text-slate-400">
                  <i className="ri-shield-check-line text-4xl mb-2 block" />
                  <p className="text-sm">No approval requests found for the selected filters</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── TAB: AUDIT TRAIL ─── */}
        {activeTab === 'audit' && (
          <div className="space-y-5">
            {/* Audit filter bar */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 items-end">
                <div>
                  <label className={lb}>Search PO Number</label>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                    <input type="text" value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)} placeholder="PO No..." className="w-full h-9 pl-8 pr-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]" />
                  </div>
                </div>
                <div>
                  <label className={lb}>Action Type</label>
                  <select value={auditActionFilter} onChange={(e) => setAuditActionFilter(e.target.value)} className={inp}>
                    <option value="ALL">All Actions</option>
                    <option value="SUBMITTED">Submitted</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="SENT_BACK">Sent Back</option>
                    <option value="ESCALATED">Escalated</option>
                    <option value="AUTO_APPROVED">Auto Approved</option>
                  </select>
                </div>
                <div className="flex items-end justify-end">
                  <button onClick={handleExportAudit} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-sm font-medium text-white hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-download-line" />Export Audit Log
                  </button>
                </div>
              </div>
            </div>

            {/* Audit table */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Date & Time', 'PO Number', 'Amount', 'Action', 'Performed By', 'Role', 'Comments'].map((h) => (
                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap ${h === 'Amount' ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {auditEvents.map((event) => (
                      <tr key={event.id} className="border-b border-slate-50 hover:bg-indigo-50/20 transition-colors">
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{formatDateTime(event.dateTime)}</td>
                        <td className="px-4 py-3 font-semibold text-[#4f46e5] whitespace-nowrap">{event.poNumber}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b] whitespace-nowrap">{formatINR(event.amount)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${AUDIT_ACTION_STYLES[event.action]}`}>
                            {AUDIT_ACTION_LABELS[event.action]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#1e293b]">{event.performedBy}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{event.role.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate">{event.comments ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {auditEvents.length === 0 && (
                <div className="text-center py-16 text-slate-400">
                  <i className="ri-history-line text-4xl mb-2 block" />
                  <p className="text-sm">No audit events found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── TAB: DEFAULT (summary cards + table for dashboard context) ─── */}
        {activeTab === 'dashboard' && (
          <></>
        )}
      </div>

      {/* Modals */}
      {approveTarget && !bulkApprove && <ApproveModal request={approveTarget} onClose={() => setApproveTarget(null)} onConfirm={handleApprove} />}
      {bulkApprove && <ApproveModal isBulk bulkCount={selectedIds.size} onClose={() => setBulkApprove(false)} onConfirm={handleBulkApprove} />}
      {rejectTarget && !bulkReject && <RejectModal request={rejectTarget} onClose={() => setRejectTarget(null)} onConfirm={handleReject} />}
      {bulkReject && <RejectModal isBulk bulkCount={selectedIds.size} onClose={() => setBulkReject(false)} onConfirm={handleBulkReject} />}
      {sendBackTarget && <SendBackModal request={sendBackTarget} onClose={() => setSendBackTarget(null)} onConfirm={handleSendBack} />}
      {detailTarget && (
        <DetailSlideOver
          request={detailTarget}
          onClose={() => setDetailTarget(null)}
          onApprove={() => { setDetailTarget(null); setTimeout(() => setApproveTarget(detailTarget), 100); }}
          onReject={() => { setDetailTarget(null); setTimeout(() => setRejectTarget(detailTarget), 100); }}
          onSendBack={() => { setDetailTarget(null); setTimeout(() => setSendBackTarget(detailTarget), 100); }}
        />
      )}
    </AppLayout>
  );
}